
import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";

// IMPORTANT: named imports
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "@tiptap/extension-table";

import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";

import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  Link as LinkIcon,
  RotateCcw,
  RotateCw,
  Grid,
  Code,
  ChevronDown,
} from "react-feather";


// ---------------------------------------------------------
// Toolbar Button
// ---------------------------------------------------------

const ToolbarButton = ({
  onClick,
  active,
  disabled,
  children,
  title,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`btn btn-sm ${
        active ? "btn-primary" : "btn-light"
      } p-1 px-2 d-inline-flex align-items-center justify-content-center`}
      style={{
        minWidth: "32px",
        height: "32px",
        border: active ? "none" : "1px solid #dee2e6",
      }}
    >
      {children}
    </button>
  );
};


// ---------------------------------------------------------
// Heading Selector Dropdown
// ---------------------------------------------------------

const HeadingSelector = ({ editor }) => {
  const [open, setOpen] = useState(false);

  const getCurrentLabel = () => {
    if (editor.isActive("heading", { level: 1 })) return "Heading 1";
    if (editor.isActive("heading", { level: 2 })) return "Heading 2";
    if (editor.isActive("heading", { level: 3 })) return "Heading 3";
    if (editor.isActive("heading", { level: 4 })) return "Heading 4";
    return "Paragraph";
  };

  return (
    <div className="position-relative d-inline-block">
      <button
        type="button"
        className="btn btn-sm btn-light border d-flex align-items-center justify-content-between gap-2 px-2"
        style={{
          height: "32px",
          minWidth: "125px",
          fontSize: "13px",
          fontWeight: 500,
          borderRadius: "6px",
        }}
        onClick={() => setOpen(!open)}
      >
        <span>{getCurrentLabel()}</span>
        <ChevronDown size={14} className="text-muted" />
      </button>

      {open && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100 z-2"
            style={{ cursor: "default" }}
            onClick={() => setOpen(false)}
          />
          <div
            className="position-absolute bg-white border rounded shadow-sm py-1 z-3 mt-1"
            style={{ width: "135px", left: 0, top: "100%" }}
          >
            <button
              type="button"
              className={`dropdown-item py-1 px-3 text-start ${
                !editor.isActive("heading") ? "active bg-primary text-white" : ""
              }`}
              style={{ fontSize: "13px" }}
              onClick={() => {
                editor.chain().focus().setParagraph().run();
                setOpen(false);
              }}
            >
              Paragraph
            </button>
            <button
              type="button"
              className={`dropdown-item py-1 px-3 text-start fw-bold ${
                editor.isActive("heading", { level: 1 })
                  ? "active bg-primary text-white"
                  : ""
              }`}
              style={{ fontSize: "13px" }}
              onClick={() => {
                editor.chain().focus().toggleHeading({ level: 1 }).run();
                setOpen(false);
              }}
            >
              Heading 1
            </button>
            <button
              type="button"
              className={`dropdown-item py-1 px-3 text-start fw-bold ${
                editor.isActive("heading", { level: 2 })
                  ? "active bg-primary text-white"
                  : ""
              }`}
              style={{ fontSize: "13px" }}
              onClick={() => {
                editor.chain().focus().toggleHeading({ level: 2 }).run();
                setOpen(false);
              }}
            >
              Heading 2
            </button>
            <button
              type="button"
              className={`dropdown-item py-1 px-3 text-start fw-bold ${
                editor.isActive("heading", { level: 3 })
                  ? "active bg-primary text-white"
                  : ""
              }`}
              style={{ fontSize: "13px" }}
              onClick={() => {
                editor.chain().focus().toggleHeading({ level: 3 }).run();
                setOpen(false);
              }}
            >
              Heading 3
            </button>
            <button
              type="button"
              className={`dropdown-item py-1 px-3 text-start fw-bold ${
                editor.isActive("heading", { level: 4 })
                  ? "active bg-primary text-white"
                  : ""
              }`}
              style={{ fontSize: "13px" }}
              onClick={() => {
                editor.chain().focus().toggleHeading({ level: 4 }).run();
                setOpen(false);
              }}
            >
              Heading 4
            </button>
          </div>
        </>
      )}
    </div>
  );
};


// ---------------------------------------------------------
// Text Editor
// ---------------------------------------------------------

const TextEditor = ({ setContent, content, placeholder }) => {
  const [showTableMenu, setShowTableMenu] = useState(false);
  const tableMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tableMenuRef.current &&
        !tableMenuRef.current.contains(event.target)
      ) {
        setShowTableMenu(false);
      }
    };

    if (showTableMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTableMenu]);

  const editor = useEditor({
    extensions: [
      StarterKit,

      Underline,

      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),

      // ---------------------------------------------------
      // Table
      // ---------------------------------------------------

      Table.configure({
        resizable: true,
      }),

      TableRow,
      TableHeader,
      TableCell,

      // ---------------------------------------------------
      // Text styling
      // ---------------------------------------------------

      TextStyle,

      Color,

      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),

      Highlight.configure({
        multicolor: true,
      }),
    ],

    content: content || "",

    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      if (setContent) {
        setContent(html);
      }
    },
  });


  // ---------------------------------------------------------
  // Sync external content
  // ---------------------------------------------------------

  useEffect(() => {
    if (!editor || content === undefined) {
      return;
    }

    const currentHTML = editor.getHTML();

    if (content !== currentHTML) {
      editor.commands.setContent(content || "");
    }
  }, [content, editor]);


  if (!editor) {
    return null;
  }


  // ---------------------------------------------------------
  // Link
  // ---------------------------------------------------------

  const setCustomLink = () => {
    const previousUrl = editor.getAttributes("link").href;

    const url = window.prompt("Enter URL", previousUrl || "");

    if (url === null) {
      return;
    }

    if (url === "") {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .unsetLink()
        .run();

      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({
        href: url,
      })
      .run();
  };


  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------

  return (
    <div className="tiptap-editor-wrapper border rounded bg-white" style={{ minHeight: '350px' }}>

      {/* =====================================================
          Styles
      ===================================================== */}

      <style>{`
        .tiptap-toolbar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 4px;
          padding: 8px 12px;
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }

        .tiptap-toolbar .vr {
          height: 24px;
          width: 1px;
          background: #dee2e6;
          margin: 0 4px;
        }

        .tiptap-content-area {
          min-height: 150px;
          max-height: 350px;
          overflow-y: auto;
          padding: 12px 16px;
        }

        .tiptap-content-area .ProseMirror {
          outline: none;
          min-height: 120px;
        }

        .tiptap-content-area .ProseMirror:focus {
          outline: none;
        }

        .tiptap-content-area .ProseMirror p {
          margin-bottom: 8px;
        }

        .tiptap-content-area .ProseMirror h1 {
          font-size: 2rem;
          font-weight: 700;
        }

        .tiptap-content-area .ProseMirror h2 {
          font-size: 1.6rem;
          font-weight: 700;
        }

        .tiptap-content-area .ProseMirror h3 {
          font-size: 1.3rem;
          font-weight: 700;
        }

        .tiptap-content-area .ProseMirror h4 {
          font-size: 1.1rem;
          font-weight: 700;
        }

        .tiptap-content-area .ProseMirror ul {
          display: block !important;
          list-style-type: disc !important;
          list-style-position: outside !important;
          padding-left: 28px !important;
          margin-top: 8px !important;
          margin-bottom: 8px !important;
        }

        .tiptap-content-area .ProseMirror ol {
          display: block !important;
          list-style-type: decimal !important;
          list-style-position: outside !important;
          padding-left: 28px !important;
          margin-top: 8px !important;
          margin-bottom: 8px !important;
        }

        .tiptap-content-area .ProseMirror li {
          display: list-item !important;
          list-style-position: outside !important;
          margin-bottom: 4px !important;
          float: none !important;
          width: auto !important;
        }

        .tiptap-content-area .ProseMirror li p {
          display: block !important;
          margin-bottom: 0 !important;
          margin-top: 0 !important;
        }

        .tiptap-content-area .ProseMirror a {
          color: #0d6efd;
          text-decoration: underline;
        }

        /* Table */

        .tiptap-content-area .ProseMirror table {
          border-collapse: collapse;
          table-layout: fixed;
          width: 100%;
          margin: 12px 0;
          overflow: hidden;
        }

        .tiptap-content-area .ProseMirror td,
        .tiptap-content-area .ProseMirror th {
          min-width: 1em;
          border: 1px solid #ced4da;
          padding: 8px 10px;
          vertical-align: top;
          box-sizing: border-box;
          position: relative;
        }

        .tiptap-content-area .ProseMirror th {
          font-weight: bold;
          text-align: left;
          background-color: #f1f3f5;
        }

        .tiptap-content-area .ProseMirror .selectedCell:after {
          z-index: 2;
          position: absolute;
          content: "";
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          background: rgba(0, 97, 205, 0.15);
          pointer-events: none;
        }

        /* Table resize handle */

        .tiptap-content-area .ProseMirror .column-resize-handle {
          position: absolute;
          right: -2px;
          top: 0;
          bottom: 0;
          width: 4px;
          background-color: #0d6efd;
          pointer-events: none;
        }

        .resize-cursor {
          cursor: ew-resize;
          cursor: col-resize;
        }

        /* Table menu */

        .table-menu {
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 1050;
          min-width: 220px;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
          padding: 8px;
          margin-top: 4px;
        }

        .table-menu button {
          font-size: 13px;
        }
      `}</style>


      {/* =====================================================
          Toolbar
      ===================================================== */}

      <div className="tiptap-toolbar">

        {/* ---------------------------------------------------
            Heading
        --------------------------------------------------- */}

        <HeadingSelector editor={editor} />


        <div className="vr" />


        {/* ---------------------------------------------------
            Bold
        --------------------------------------------------- */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleBold().run()
          }
          active={editor.isActive("bold")}
          title="Bold"
        >
          <BoldIcon size={14} />
        </ToolbarButton>


        {/* Italic */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleItalic().run()
          }
          active={editor.isActive("italic")}
          title="Italic"
        >
          <ItalicIcon size={14} />
        </ToolbarButton>


        {/* Underline */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleUnderline().run()
          }
          active={editor.isActive("underline")}
          title="Underline"
        >
          <UnderlineIcon size={14} />
        </ToolbarButton>


        {/* Strike */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleStrike().run()
          }
          active={editor.isActive("strike")}
          title="Strikethrough"
        >
          <span
            style={{
              textDecoration: "line-through",
              fontWeight: "bold",
              fontSize: "13px",
            }}
          >
            S
          </span>
        </ToolbarButton>


        {/* Code */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleCode().run()
          }
          active={editor.isActive("code")}
          title="Code"
        >
          <Code size={14} />
        </ToolbarButton>


        {/* Highlight */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHighlight().run()
          }
          active={editor.isActive("highlight")}
          title="Highlight"
        >
          <span
            style={{
              backgroundColor: editor.isActive("highlight") ? "transparent" : "#ffe066",
              color: "#000",
              padding: "0 4px",
              borderRadius: "2px",
              fontWeight: "bold",
              fontSize: "12px",
              lineHeight: "1",
            }}
          >
            H
          </span>
        </ToolbarButton>


        <div className="vr" />


        {/* ---------------------------------------------------
            Alignment
        --------------------------------------------------- */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().setTextAlign("left").run()
          }
          active={editor.isActive({ textAlign: "left" })}
          title="Align Left"
        >
          <AlignLeft size={14} />
        </ToolbarButton>


        <ToolbarButton
          onClick={() =>
            editor.chain().focus().setTextAlign("center").run()
          }
          active={editor.isActive({ textAlign: "center" })}
          title="Align Center"
        >
          <AlignCenter size={14} />
        </ToolbarButton>


        <ToolbarButton
          onClick={() =>
            editor.chain().focus().setTextAlign("right").run()
          }
          active={editor.isActive({ textAlign: "right" })}
          title="Align Right"
        >
          <AlignRight size={14} />
        </ToolbarButton>


        <ToolbarButton
          onClick={() =>
            editor.chain().focus().setTextAlign("justify").run()
          }
          active={editor.isActive({ textAlign: "justify" })}
          title="Align Justify"
        >
          <AlignJustify size={14} />
        </ToolbarButton>


        <div className="vr" />


        {/* ---------------------------------------------------
            Lists
        --------------------------------------------------- */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleBulletList().run()
          }
          active={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <List size={14} />
        </ToolbarButton>


        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleOrderedList().run()
          }
          active={editor.isActive("orderedList")}
          title="Ordered List"
        >
          <span
            style={{
              fontWeight: "bold",
              fontSize: "12px",
            }}
          >
            1.
          </span>
        </ToolbarButton>


        <div className="vr" />


        {/* ---------------------------------------------------
            Link
        --------------------------------------------------- */}

        <ToolbarButton
          onClick={setCustomLink}
          active={editor.isActive("link")}
          title="Link"
        >
          <LinkIcon size={14} />
        </ToolbarButton>


        {/* ---------------------------------------------------
            Text Color
        --------------------------------------------------- */}

        <input
          type="color"
          className="form-control form-control-color p-0 border-0"
          style={{
            width: "28px",
            height: "28px",
            cursor: "pointer",
          }}
          value={
            editor.getAttributes("textStyle").color ||
            "#000000"
          }
          onChange={(e) =>
            editor
              .chain()
              .focus()
              .setColor(e.target.value)
              .run()
          }
          title="Text Color"
        />


        {/* ---------------------------------------------------
            Highlight Color
        --------------------------------------------------- */}

        <input
          type="color"
          className="form-control form-control-color p-0 border-0"
          style={{
            width: "28px",
            height: "28px",
            cursor: "pointer",
          }}
          value={
            editor.getAttributes("highlight").color ||
            "#ffff00"
          }
          onChange={(e) =>
            editor
              .chain()
              .focus()
              .toggleHighlight({ color: e.target.value })
              .run()
          }
          title="Highlight Color"
        />


        <div className="vr" />


        {/* ===================================================
            TABLE
        =================================================== */}

        <div className="position-relative d-inline-block" ref={tableMenuRef}>

          <ToolbarButton
            onClick={() =>
              setShowTableMenu(!showTableMenu)
            }
            active={editor.isActive("table")}
            title="Table"
          >
            <Grid size={14} />
          </ToolbarButton>


          {showTableMenu && (
            <div className="table-menu" style={
              {
                overflowY: "auto",
                height: "auto",
                maxHeight: "200px",
              }
            }>

              {/* Insert Table */}

              <button
                type="button"
                className="btn btn-sm btn-outline-primary w-100 text-start mb-1"
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .insertTable({
                      rows: 2,
                      cols: 2,
                      withHeaderRow: true,
                    })
                    .run();

                  setShowTableMenu(false);
                }}
              >
                Insert Table 2 × 2
              </button>


              <div className="dropdown-divider" />


              {/* Add Column Before */}

              <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().addColumnBefore()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .addColumnBefore()
                    .run()
                }
              >
                Add Column Before
              </button>


              {/* Add Column After */}

              <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().addColumnAfter()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .addColumnAfter()
                    .run()
                }
              >
                Add Column After
              </button>


              {/* Delete Column */}

              <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().deleteColumn()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .deleteColumn()
                    .run()
                }
              >
                Delete Column
              </button>


              <div className="dropdown-divider" />


              {/* Add Row Before */}

              <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().addRowBefore()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .addRowBefore()
                    .run()
                }
              >
                Add Row Before
              </button>


              {/* Add Row After */}

              <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().addRowAfter()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .addRowAfter()
                    .run()
                }
              >
                Add Row After
              </button>


              {/* Delete Row */}

              <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().deleteRow()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .deleteRow()
                    .run()
                }
              >
                Delete Row
              </button>


              <div className="dropdown-divider" />


              {/* Merge Cells */}

              {/* <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().mergeCells()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .mergeCells()
                    .run()
                }
              >
                Merge Cells
              </button> */}


              {/* Split Cell */}

              {/* <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().splitCell()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .splitCell()
                    .run()
                }
              >
                Split Cell
              </button> */}


              {/* Toggle Header Row */}

              <button
                type="button"
                className="btn btn-sm btn-light w-100 text-start mb-1"
                disabled={!editor.can().toggleHeaderRow()}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .toggleHeaderRow()
                    .run()
                }
              >
                Toggle Header Row
              </button>


              <div className="dropdown-divider" />


              {/* Delete Table */}

              <button
                type="button"
                className="btn btn-sm btn-outline-danger w-100 text-start"
                disabled={!editor.can().deleteTable()}
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .deleteTable()
                    .run();

                  setShowTableMenu(false);
                }}
              >
                Delete Table
              </button>

            </div>
          )}

        </div>


        <div className="vr" />


        {/* ---------------------------------------------------
            Undo
        --------------------------------------------------- */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().undo().run()
          }
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <RotateCcw size={14} />
        </ToolbarButton>


        {/* ---------------------------------------------------
            Redo
        --------------------------------------------------- */}

        <ToolbarButton
          onClick={() =>
            editor.chain().focus().redo().run()
          }
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <RotateCw size={14} />
        </ToolbarButton>

      </div>


      {/* =====================================================
          Editor
      ===================================================== */}

      <div className="tiptap-content-area">
        <EditorContent editor={editor} />
      </div>

    </div>
  );
};


export default TextEditor;
